import React, { useState, useRef } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://52.5.52.241:8765'; // Backend server endpoint

// Define interfaces for API responses
interface SBSCode {
  service_identified?: string;
  sbs_code: string;
  sbs_code_hyphenated: string;
  description: string;
  chapter: string;
  block: string;
}

interface DiagnosisResult {
  confirmed_diagnoses: string[];
  ruled_out_diagnoses: string[];
  icd_codes: string[];
  standard?: string;
  sbs_codes?: SBSCode[];
  // New properties to store diagnosis-code pairs
  confirmed_diagnosis_codes?: Array<{diagnosis: string; code: string}>;
  ruled_out_diagnosis_codes?: Array<{diagnosis: string; code: string}>;
}

interface FileResult extends DiagnosisResult {
  file_index: number;
  filename: string;
}

interface FileError {
  file_index: number;
  filename: string;
  error: string;
}

interface MultipleFilesResponse {
  results: FileResult[];
  errors: FileError[];
  total_files: number;
  successful_files: number;
  failed_files: number;
  standard?: string; // Add standard field that may be returned from the API
}

// ICD standards enum
type ICDStandard = 'cm' | 'am';

const App: React.FC = () => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagnosisResult|null>(null);
  const [multipleResults, setMultipleResults] = useState<MultipleFilesResponse|null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedStandard, setSelectedStandard] = useState<ICDStandard>('cm'); // Default to CM
  const [extractSBS, setExtractSBS] = useState(false); // Add state for SBS extraction
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setText(textarea.value);
    
    // Reset height to auto to properly calculate scroll height
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Process the diagnoses and ICD codes to create paired arrays
  const processDiagnosisResult = (result: DiagnosisResult): DiagnosisResult => {
    // For simplicity, we'll assume ICD codes are in the same order as diagnoses
    // In a real-world scenario, you might need a more robust matching algorithm
    
    const confirmed_diagnosis_codes = result.confirmed_diagnoses.map((diagnosis, index) => {
      const code = index < result.icd_codes?.length ? result.icd_codes[index] : '';
      return { diagnosis, code };
    });
    
    // If there are no ruled out diagnoses, we'll skip creating those pairs
    let ruled_out_diagnosis_codes: Array<{diagnosis: string; code: string}> = [];
    
    if (result.ruled_out_diagnoses.length > 0) {
      ruled_out_diagnosis_codes = result.ruled_out_diagnoses.map((diagnosis, index) => {
        // Calculate the index in the ICD codes array, accounting for confirmed diagnoses
        const offsetIndex = index + result.confirmed_diagnoses.length;
        const code = offsetIndex < result.icd_codes.length ? result.icd_codes[offsetIndex] : '';
        return { diagnosis, code };
      });
    }
    
    return {
      ...result,
      confirmed_diagnosis_codes,
      ruled_out_diagnosis_codes
    };
  };

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) {
      setError('Please enter some text');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setMultipleResults(null);
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('standard', selectedStandard); // Add selected standard
      formData.append('extract_sbs', extractSBS.toString()); // Add SBS extraction flag
      
      const res = await fetch(`${API_URL}/extract_icd_codes/`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Failed to fetch results');
      const data = await res.json();
      setResult(processDiagnosisResult(data));
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing your request');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setLoading(true);
    setError(null);
    setResult(null);
    setMultipleResults(null);
    
    try {
      const formData = new FormData();
      // Append each file to the FormData with the same key name 'files'
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      // Add selected standard
      formData.append('standard', selectedStandard);
      
      // Add SBS extraction flag
      formData.append('extract_sbs', extractSBS.toString());
      
      const res = await fetch(`${API_URL}/extract_icd_codes_multiple_files/`, {
        method: 'POST',
        body: formData,
      });
      
      if (!res.ok) throw new Error('Failed to fetch results');
      const data: MultipleFilesResponse = await res.json();
      
      // Process each result to pair diagnoses with codes
      const processedData: MultipleFilesResponse = {
        ...data,
        results: data.results.map(result => ({
          ...result,
          ...processDiagnosisResult(result)
        }))
      };
      
      setMultipleResults(processedData);
      
      // If only one file was uploaded, also set the single result for backward compatibility
      if (processedData.results.length === 1) {
        setResult(processedData.results[0]);
      }
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing your files');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format and display diagnosis text that may contain Arabic+English
  const FormatDiagnosis = ({ text }: { text: string }) => {
    // Check if the diagnosis follows our Arabic+English format
    if (text.includes("Arabic:") && text.includes("English:")) {
      // Split by the delimiter
      const parts = text.split("|");
      if (parts.length === 2) {
        const arabicPart = parts[0].trim();
        const englishPart = parts[1].trim();
        
        return (
          <div className="bilingual-diagnosis">
            <div className="arabic-part" dir="rtl">{arabicPart}</div>
            <div className="english-part" dir="ltr">{englishPart}</div>
          </div>
        );
      }
    }
    
    // If not in the special format, just render as normal with auto direction
    return <span dir="auto">{text}</span>;
  };

  const renderSingleResult = (data: DiagnosisResult) => {
    // Check if there are any ruled out diagnoses
    const hasRuledOutDiagnoses = data.ruled_out_diagnoses.length > 0;
    
    // If no ruled out diagnoses, use the original three-box layout
    if (!hasRuledOutDiagnoses) {
      return (
        <div className="results-section">
          <h2>Extracted Information {data.standard && `(${data.standard})`}</h2>
          <div className="results-grid">
            <div className="result-card">
              <h3>Confirmed Diagnoses</h3>
              <ul>
                {data.confirmed_diagnoses.length ? 
                  data.confirmed_diagnoses.map((d, i) => (
                    <li key={i} className="diagnosis-item">
                      <FormatDiagnosis text={d} />
                    </li>
                  )) : 
                  <li className="no-results">No confirmed diagnoses found</li>
                }
              </ul>
            </div>
            <div className="result-card">
              <h3>ICD Codes</h3>
              <ul>
                {data.icd_codes.length ? 
                  data.icd_codes.map((c, i) => <li key={i} dir="auto">{c}</li>) : 
                  <li className="no-results">No ICD codes found</li>
                }
              </ul>
            </div>
          </div>
        </div>
      );
    }
    
    // If there are ruled out diagnoses, use the new layout with paired diagnoses and codes
    return (
      <div className="results-section">
        <h2>Extracted Information {data.standard && `(${data.standard})`}</h2>
        <div className="results-grid">
          <div className="result-card">
            <h3>Confirmed Diagnoses</h3>
                          <ul>
                {data.confirmed_diagnosis_codes?.length ? 
                  data.confirmed_diagnosis_codes.map((item: {diagnosis: string; code: string}, i: number) => (
                  <li key={i} className="diagnosis-item">
                    <div className="diagnosis-with-code">
                      <div className="diagnosis-text">
                        <FormatDiagnosis text={item.diagnosis} />
                      </div>
                      {item.code && <div className="icd-code" dir="auto">— {item.code}</div>}
                    </div>
                  </li>
                )) : 
                <li className="no-results">No confirmed diagnoses found</li>
              }
            </ul>
          </div>
          <div className="result-card ruled-out">
            <h3>Ruled Out Diagnoses</h3>
                          <ul>
                {data.ruled_out_diagnosis_codes?.length ? 
                  data.ruled_out_diagnosis_codes.map((item: {diagnosis: string; code: string}, i: number) => (
                  <li key={i} className="diagnosis-item">
                    <div className="diagnosis-with-code">
                      <div className="diagnosis-text">
                        <FormatDiagnosis text={item.diagnosis} />
                      </div>
                      {item.code && <div className="icd-code" dir="auto">— {item.code}</div>}
                    </div>
                  </li>
                )) : 
                <li className="no-results">No ruled out diagnoses found</li>
              }
            </ul>
          </div>
        </div>
        {data.sbs_codes && data.sbs_codes.length > 0 && (
          <div className="result-card sbs-codes">
            <h3>SBS Codes</h3>
            <ul>
              {data.sbs_codes.map((sbs, i) => (
                <li key={i} className="sbs-item">
                  <div className="sbs-code-header">
                    <strong>{sbs.sbs_code_hyphenated}</strong>
                    {sbs.service_identified && <span className="service-identified"> - {sbs.service_identified}</span>}
                  </div>
                  <div className="sbs-description">{sbs.description}</div>
                  <div className="sbs-meta">
                    <span className="sbs-chapter">{sbs.chapter}</span>
                    <span className="sbs-block">{sbs.block}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderMultipleResults = (data: MultipleFilesResponse) => (
    <div className="results-section">
      <h2>Extraction Results ({data.successful_files} of {data.total_files} files processed successfully) 
        {data.standard && ` - ${data.standard}`}
      </h2>
      
      {data.errors.length > 0 && (
        <div className="errors-section">
          <h3>Errors ({data.errors.length})</h3>
          <ul className="error-list">
            {data.errors.map((err, idx) => (
              <li key={`error-${idx}`} className="error-item">
                <strong dir="auto">{err.filename}</strong>: {err.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {data.results.map((fileResult, idx) => {
        // Check if there are any ruled out diagnoses for this file
        const hasRuledOutDiagnoses = fileResult.ruled_out_diagnoses.length > 0;
        
        return (
          <div key={`result-${idx}`} className="file-result">
            <h3 className="file-name" dir="auto">{fileResult.filename}</h3>
            
            {!hasRuledOutDiagnoses ? (
              // Original three-box layout if no ruled out diagnoses
              <div className="results-grid">
                <div className="result-card">
                  <h4>Confirmed Diagnoses</h4>
                  <ul>
                    {fileResult.confirmed_diagnoses.length ? 
                      fileResult.confirmed_diagnoses.map((d, i) => (
                        <li key={i} className="diagnosis-item">
                          <FormatDiagnosis text={d} />
                        </li>
                      )) : 
                      <li className="no-results">No confirmed diagnoses found</li>
                    }
                  </ul>
                </div>
                <div className="result-card">
                  <h4>ICD Codes</h4>
                  <ul>
                    {fileResult.icd_codes.length ? 
                      fileResult.icd_codes.map((c, i) => <li key={i} dir="auto">{c}</li>) : 
                      <li className="no-results">No ICD codes found</li>
                    }
                  </ul>
                </div>
              </div>
            ) : (
              // New layout with paired diagnoses and codes if there are ruled out diagnoses
              <div className="results-grid">
                <div className="result-card">
                  <h4>Confirmed Diagnoses</h4>
                  <ul>
                    {fileResult.confirmed_diagnosis_codes?.length ? 
                      fileResult.confirmed_diagnosis_codes.map((item, i) => (
                        <li key={i} className="diagnosis-item">
                          <div className="diagnosis-with-code">
                            <div className="diagnosis-text">
                              <FormatDiagnosis text={item.diagnosis} />
                            </div>
                            {item.code && <div className="icd-code" dir="auto">— {item.code}</div>}
                          </div>
                        </li>
                      )) : 
                      <li className="no-results">No confirmed diagnoses found</li>
                    }
                  </ul>
                </div>
                <div className="result-card ruled-out">
                  <h4>Ruled Out Diagnoses</h4>
                  <ul>
                    {fileResult.ruled_out_diagnosis_codes?.length ? 
                      fileResult.ruled_out_diagnosis_codes.map((item, i) => (
                        <li key={i} className="diagnosis-item">
                          <div className="diagnosis-with-code">
                            <div className="diagnosis-text">
                              <FormatDiagnosis text={item.diagnosis} />
                            </div>
                            {item.code && <div className="icd-code" dir="auto">— {item.code}</div>}
                          </div>
                        </li>
                      )) : 
                      <li className="no-results">No ruled out diagnoses found</li>
                    }
                  </ul>
                </div>
              </div>
            )}
            
            {/* Add SBS codes section for each file */}
            {fileResult.sbs_codes && fileResult.sbs_codes.length > 0 && (
              <div className="result-card sbs-codes" style={{marginTop: '1rem'}}>
                <h4>SBS Codes</h4>
                <ul>
                  {fileResult.sbs_codes.map((sbs, i) => (
                    <li key={i} className="sbs-item">
                      <div className="sbs-code-header">
                        <strong>{sbs.sbs_code_hyphenated}</strong>
                        {sbs.service_identified && <span className="service-identified"> - {sbs.service_identified}</span>}
                      </div>
                      <div className="sbs-description">{sbs.description}</div>
                      <div className="sbs-meta">
                        <span className="sbs-chapter">{sbs.chapter}</span>
                        <span className="sbs-block">{sbs.block}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>Medical Text to ICD Code Extractor</h1>
        <p className="subtitle">Extract ICD-10 codes from medical text or documents</p>
      </header>

      <main className="main-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {multipleResults ? 
          renderMultipleResults(multipleResults) : 
          (result && renderSingleResult(result))
        }

        <div className="message-input-container">
          <form onSubmit={handleTextSubmit} className="message-form">
            {/* ICD Standard Selection */}
            <div className="standard-selection">
              <label className="standard-radio">
                <input
                  type="radio"
                  name="icdStandard"
                  value="cm"
                  checked={selectedStandard === 'cm'}
                  onChange={() => setSelectedStandard('cm')}
                />
                <span>ICD-10-CM</span>
              </label>
              <label className="standard-radio">
                <input
                  type="radio"
                  name="icdStandard"
                  value="am"
                  checked={selectedStandard === 'am'}
                  onChange={() => setSelectedStandard('am')}
                />
                <span>ICD-10-AM</span>
              </label>
              <label className="standard-checkbox">
                <input
                  type="checkbox"
                  checked={extractSBS}
                  onChange={(e) => setExtractSBS(e.target.checked)}
                />
                <span>Extract SBS Codes</span>
              </label>
            </div>
          
            <button 
              type="button" 
              className="upload-button"
              onClick={() => fileInputRef.current?.click()}
              title="Upload Documents"
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
            
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextareaInput}
              placeholder="Write a message..."
              rows={1}
              className="message-input"
              dir="auto"
            />
            
            <button 
              type="submit" 
              className="send-button"
              disabled={loading || !text.trim()}
            >
              {loading ? (
                <span className="loading-spinner" />
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </form>
        </div>

        {/* Hidden file input - now with multiple attribute */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          multiple
          accept=".pdf,.txt"
        />
      </main>
    </div>
  );
};

export default App;
